import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, ShieldAlert } from "lucide-react";
import { LOGO_URL, BRAND } from "../lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CoverageAction() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [instructions, setInstructions] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!token) {
      setError("Missing action token in the link.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/coverage/magic-preview`, { params: { token } });
        if (cancelled) return;
        setPreview(data);
      } catch (err) {
        if (cancelled) return;
        setError(err?.response?.data?.detail || "This approval link is invalid or expired.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const body = { token };
      if (preview.action === "approve") body.instructions = instructions;
      if (preview.action === "reject") body.reason = reason;
      const { data } = await axios.post(`${API}/coverage/magic-action`, body);
      setDone(data);
      if (data.already) {
        toast.message(data.message || `Already ${data.status}`);
      } else {
        toast.success(
          preview.action === "approve"
            ? `Approved — confirmation emailed to ${data.doctor_email}`
            : `Rejected — notice emailed to ${data.doctor_email}`
        );
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not complete action");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-xl">
        <Link to="/" className="block mb-8 text-center">
          <img src={LOGO_URL} alt="GCOMS" className="w-14 h-14 mx-auto rounded-full bg-black p-1" />
          <div className="font-heading text-xl font-bold tracking-tight mt-3">{BRAND}</div>
        </Link>

        {loading && (
          <div className="module-card p-10 bg-white text-center" data-testid="cov-action-loading">
            <Loader2 className="mx-auto animate-spin text-[#F26A24]" size={28} />
            <div className="mt-3 text-sm text-muted-foreground">Verifying the action link…</div>
          </div>
        )}

        {!loading && error && (
          <div className="module-card p-10 bg-white text-center" data-testid="cov-action-error">
            <ShieldAlert className="mx-auto text-destructive" size={32} />
            <div className="font-heading text-xl font-semibold mt-3">Link not valid</div>
            <p className="text-sm text-muted-foreground mt-2">{error}</p>
            <button onClick={() => navigate("/admin")} data-testid="cov-action-error-cta" className="mt-6 px-5 py-2.5 rounded-md bg-ink text-bone text-sm font-medium hover:bg-ink/90">Open admin console</button>
          </div>
        )}

        {!loading && !error && preview && !done && (
          <div className="module-card p-8 bg-white" data-testid="cov-action-card">
            <div className="uppercase-label">Coverage decision</div>
            <h1 className="font-heading text-2xl font-semibold mt-2" data-testid="cov-action-headline">
              {preview.action === "approve" ? "Approve this coverage request" : "Reject this coverage request"}
            </h1>
            <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
              <Cell label="Doctor (away)" value={preview.request.away_doctor_name} />
              <Cell label="Practice" value={preview.request.practice} />
              <Cell label="Start" value={preview.request.start_date} />
              <Cell label="End" value={preview.request.end_date} />
              <Cell label="Contact email" value={preview.request.contact_email} mono />
              <Cell label="Forwarding phone" value={preview.request.forwarding_phone || "—"} />
              <Cell label="Scope" value={(preview.request.coverage_scope || []).join(", ") || "—"} wide />
              {preview.request.notes && <Cell label="Notes" value={preview.request.notes} wide />}
            </div>

            {preview.action === "approve" ? (
              <div className="mt-6">
                <div className="text-sm font-medium mb-1">Instructions for the doctor's patients & staff</div>
                <div className="text-xs text-muted-foreground mb-2">Sent in the confirmation email. Leave blank to use the default message.</div>
                <textarea
                  data-testid="cov-action-instructions"
                  rows={5}
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  placeholder={`Sample: During ${preview.request.start_date}–${preview.request.end_date}, please forward all post-op, implant, and emergency calls to our office at 516-352-7888. We'll triage and respond directly.`}
                  className="w-full px-3 py-2 rounded-md border border-gray-200 text-sm focus:outline-none focus:border-[#F26A24]"
                />
              </div>
            ) : (
              <div className="mt-6">
                <div className="text-sm font-medium mb-1">Reason (optional, shown to the doctor)</div>
                <textarea
                  data-testid="cov-action-reason"
                  rows={4}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Sample: Our team is already covering another practice during those dates. Please reach out to discuss alternative arrangements."
                  className="w-full px-3 py-2 rounded-md border border-gray-200 text-sm focus:outline-none focus:border-[#F26A24]"
                />
              </div>
            )}

            <div className="mt-6 flex items-center gap-3">
              <button
                data-testid="cov-action-submit"
                onClick={submit}
                disabled={submitting}
                className={`px-6 py-3 rounded-md text-white font-medium disabled:opacity-60 ${preview.action === "approve" ? "bg-sage-deep hover:bg-sage-deep/90" : "bg-destructive hover:bg-destructive/90"}`}
              >
                {submitting ? "Sending…" : preview.action === "approve" ? "Approve & send confirmation" : "Reject & notify doctor"}
              </button>
              <Link to="/admin" data-testid="cov-action-cancel" className="text-sm text-muted-foreground hover:text-ink">Cancel</Link>
            </div>
          </div>
        )}

        {!loading && done && (
          <div className="module-card p-10 bg-white text-center" data-testid="cov-action-done">
            {preview.action === "approve" ? (
              <CheckCircle2 className="mx-auto text-sage-deep" size={36} />
            ) : (
              <XCircle className="mx-auto text-destructive" size={36} />
            )}
            <div className="font-heading text-xl font-semibold mt-3">
              {done.already
                ? done.message
                : preview.action === "approve"
                ? "Coverage approved"
                : "Coverage request rejected"}
            </div>
            {!done.already && (
              <p className="text-sm text-muted-foreground mt-2">
                Confirmation email {done.emailed ? "sent" : "could not be sent"} to <span className="font-mono">{done.doctor_email}</span>.
              </p>
            )}
            <Link to="/admin" data-testid="cov-action-finish-cta" className="inline-block mt-6 px-5 py-2.5 rounded-md bg-ink text-bone text-sm font-medium hover:bg-ink/90">Open admin console</Link>
          </div>
        )}
      </div>
    </div>
  );
}

function Cell({ label, value, mono, wide }) {
  return (
    <div className={wide ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`mt-0.5 text-sm ${mono ? "font-mono" : ""} break-words`}>{value || "—"}</div>
    </div>
  );
}
